import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import moment from 'moment-timezone';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { LocalizationReplacementsModel } from '../models/localization-replacements.model';
import type { LanguageModel } from '../../../Core/Localization/models/language.model';
import { availableLanguages } from '../../../Core/Localization/models/available-languages.model';
import { CurrentUserStateService } from '../../Users/services/current-user-state.service';
import { LanguageTagEnum } from '../enums/language-tag.enum';
import { languageDataLocalizationMap } from '../language-localization.model';
import { LOCALIZATION_RESOURCE } from '../../../Core/Localization/tokens/localization.token';
import { LanguageResourceType } from '../../../Core/Localization/types/language-resource-config.type';
import { LanguageResourceLoader } from '../../../Core/Localization/types/language-resource-loader.type';

type LanguageMap = Map<string, string>;

@Injectable({
    providedIn: 'root'
})
export class LocalizationService implements LanguageResourceLoader {
    private static readonly defaultLanguageTag = LanguageTagEnum.en_US;
    private readonly sessionLanguageKey = 'LANGUAGE_MAPS';
    private readonly sessionKey = 'LOCALIZATION';
    private initialized = false;
    private langLocalizationMap: Record<LanguageTagEnum, Record<string, string> | null> = {
        [LanguageTagEnum.en_GB]: null,
        [LanguageTagEnum.en_US]: null,
        [LanguageTagEnum.fr_CA]: null,
        [LanguageTagEnum.fr_FR]: null,
    };
    private readonly DEFAULT_LANG_SOURCE: LanguageResourceType = {
        [LanguageTagEnum.en_GB]: () => import('../localization.en.model'),
        [LanguageTagEnum.en_US]: () => import('../localization.en.model'),
        [LanguageTagEnum.fr_CA]: () => import('../localization.fr-CA.model'),
        [LanguageTagEnum.fr_FR]: () => import('../localization.fr-FR.model'),
    }
    private frLocalization: LanguageMap;
    private availableLanguages$: BehaviorSubject<LanguageModel[]> = new BehaviorSubject<LanguageModel[]>(availableLanguages);
    private dataReferenceTag: string = LocalizationService.defaultLanguageTag;
    private dataReferenceLanguageMap: LanguageMap;

    readonly availableLanguages: LanguageModel[] = availableLanguages;
    localizationMapChangeProvider: Subject<void>;
    localization: LanguageMap;
    defaultLocalization: LanguageMap;
    fallbackFrLocalization: LanguageMap;
    uiLanguageTag: LanguageTagEnum;

    constructor(
        private http: HttpClient,
        private userStateService: CurrentUserStateService,
        @Inject(LOCALIZATION_RESOURCE) private readonly LANG_SOURCE?: LanguageResourceType
    ) {
        this.localizationMapChangeProvider = new BehaviorSubject<void>(null);

    }

    public async initLocalization() {
        if (this.initialized) {
            return;
        }
        await Promise.allSettled([
            this.loadLangSource(LanguageTagEnum.en_US),
            this.loadLangSource(LanguageTagEnum.fr_CA),
            this.loadLangSource(LanguageTagEnum.fr_FR)
        ]);

        this.defaultLocalization = this.makeMapFromJson(this.getLangSource(LocalizationService.defaultLanguageTag));
        this.initLocalizationListeners();

        this.initialized = true;
    }


    private async loadLangSource(lang: LanguageTagEnum) {
        if (this.langLocalizationMap[lang]) {
            return;
        }
        // If a local LANG_SOURCE is not provided, use the core default DEFAULT_LANG_SOURCE.
        const source = this.LANG_SOURCE || this.DEFAULT_LANG_SOURCE;
        const module: any = await source[lang]();
        let langSource = null;
        switch (lang) {
            case LanguageTagEnum.en_US:
            case LanguageTagEnum.en_GB:
                langSource = module.localizationEn;
                break;
            case LanguageTagEnum.fr_CA:
                langSource = module.localizationFrCA;
                break;
            case LanguageTagEnum.fr_FR:
                langSource = module.localizationFrFR;
                break;
        }
        this.langLocalizationMap[lang] = langSource;
    }

    private getLangSource(lang: LanguageTagEnum): Record<string, string> {
        return this.langLocalizationMap[lang] ?? {};
    }

    private initLocalizationListeners() {
        this.userStateService.getUILanguageTag().subscribe(languageTag => {
            if (languageTag) {
                this.setLocalization(languageTag as LanguageTagEnum);
            } else {
                this.setLocalization(LocalizationService.defaultLanguageTag);
            }

            // language is set to fr_FR, set fallback localization to fr_CA
            if (languageTag === LanguageTagEnum.fr_FR) {
                this.fallbackFrLocalization = this.makeMapFromJson(this.getLangSource(LanguageTagEnum.fr_CA));
                moment.locale('fr');
                // language is set to fr_CA, set fallback localization to fr_FR
            } else if (languageTag === LanguageTagEnum.fr_CA) {
                this.fallbackFrLocalization = this.makeMapFromJson(this.getLangSource(LanguageTagEnum.fr_FR));
                moment.locale('fr');
            } else {
                moment.locale('en');
            }
        });
        this.userStateService.getDataReferenceLanguageTag().subscribe(languageTag => {
            this.dataReferenceTag = languageTag ?? LocalizationService.defaultLanguageTag;
            this.dataReferenceLanguageMap = this.makeMapFromJson(languageDataLocalizationMap);
        });
    }

    /**
     * Returns an observable to provide latest Localization Map
     */
    getLocalizationMapChange(): Observable<void> {
        return this.localizationMapChangeProvider;
    }

    /**
     * Obtains a Map<string, string> to be provided by the localization Provider
     * Currently pulls the Map either from sessionStorage or from a json file
     */
    setLocalization(languageTag: LanguageTagEnum): void {
        this.uiLanguageTag = languageTag;

        this.localization = this.makeMapFromJson(this.getLangSource(languageTag));
        this.localizationMapChangeProvider.next();
        /*
        This is commented out until the back end can provide the JSON object as a route

        this.http.get('./assets/localization' + lang + '.json').subscribe(
            (jsonObjFromFile: JSON) => {
                this.localization = this.makeMapFromJson(jsonObjFromFile);
                this.localizationMapChangeProvider.next();
                this.setSessionStorage(jsonObjFromFile);
            }
        );
        */
    }

    /**
     * Converts a JSON object to a map of <string, string>
     * @param jsonObj - JSON Object
     */
    makeMapFromJson(jsonObj): LanguageMap {
        const localization = new Map<string, string>();
        Object.keys(jsonObj).forEach(
            (each) => {
                if ( typeof(jsonObj[each]) === 'string' ) {
                    localization.set(each, jsonObj[each]);
                }
            }
        );
        return localization;
    }

    /**
     * Performs a transform operation on a localization key to get the string for the current language
     *
     * @param text Either a key to be looked up from a cache/ backend / file localizationMap or a string to be returned
     * @param args (OPTIONAL) Parameters to replace arguments in text
     *
     * @returns Translated string
     */
    /* eslint-disable @typescript-eslint/naming-convention */
    transform(textOrNumber: string | number, args?: LocalizationReplacementsModel): string {
        if (!textOrNumber && textOrNumber !== 0) {
            return '';
        }
        const text = '' + textOrNumber;

        if (args == null) {
            args = { '' : ''};
        }
        if (this.localization == null) {
            return '';
        }
        let result = text;

        let localized: string = this.localization.get(text);

        // TODO: if we ever add more languages, we might want to come up with a smarter way to have some sort of default fallback system

        // since we have 2 different french localizations, sometimes we set the translation in 1 file, but not the other
        // fix is to have a both french fallback on one another before trying the default english
        if (
            this.isNotLocalized(localized) &&
            ((this.uiLanguageTag === LanguageTagEnum.fr_FR) || (this.uiLanguageTag === LanguageTagEnum.fr_CA))
        ) {
            localized = this.fallbackFrLocalization?.get(text);
        }

        if (this.isNotLocalized(localized)) {
            localized = this.defaultLocalization.get(text);
        }

        if (localized || localized === '') {
            result = this.parseArgsFromText(localized, args);
        }

        return result;
    }

    /**
     * returns true if the string being passed is set to null, undefined or empty string
     * @param localized is the localized value of the string we are trying to translate
     * @returns return true or false
     */
    isNotLocalized(localized: string) {
        return (localized === null || localized === undefined || localized === '');
    }

    /**
     * Translates a key using a specific locale string ('en' or 'fr'), independent of the global UI language.
     * Used for block-level language settings (e.g. Canvas Briefings).
     */
    transformWithLocale(key: string, locale: string): string {
        if (!locale?.includes('fr')) {
            return this.defaultLocalization.get(key) || key;
        }
        if (!this.frLocalization) {
            this.frLocalization = this.makeMapFromJson(this.getLangSource(LanguageTagEnum.fr_CA));
        }
        let result = this.frLocalization.get(key);
        if (this.isNotLocalized(result)) {
            result = this.makeMapFromJson(this.getLangSource(LanguageTagEnum.fr_FR)).get(key);
        }
        if (this.isNotLocalized(result)) {
            result = this.defaultLocalization.get(key);
        }
        return result || key;
    }

    /**
     * Performs a transform operation on a series of localization keys to get the strings for the current language
     */
    transformList(items: string[] | any[], args: LocalizationReplacementsModel[] = [], key: string): string[]|any[] {
        const output = [];

        for (let i = 0, l = items.length; i < l; i++) {
            let item = items[i];

            if (item) {
                const arg = args[i];

                if (key) {
                    item = this.shallowClone(item);
                    item[key] = this.transform(item[key], arg);
                } else {
                    item = this.transform(item, arg);
                }
            }

            output.push(item);
        }

        return output;
    }

    /**
     * Performs a shallow clone on an object, retaining it's type
     */
    shallowClone(obj: any): any {
        // Using the contructor of the object to retain the type and methods etc.
        const clone = new obj.constructor();

        // Copy each of the properties of the original onto the clone
        // @TODO replace this loop with Object.assign when we no longer need to support IE11
        for (const i in obj) {
            if (obj.hasOwnProperty(i)) {
                clone[i] = obj[i];
            }
        }

        return clone;
    }

    getAvailableLanguagesByDataReference(): Observable<LanguageModel[]> {
        return this.availableLanguages$.pipe(
            filter(x => !!x),
            map((languages: LanguageModel[]) => {
                return languages.map(language => {
                    const mapKey = `${this.dataReferenceTag}.${language.id}`;
                    const newName = this.dataReferenceLanguageMap.get(mapKey);
                    if (newName !== '') {
                        language.name = newName;
                    }
                    return language;
                });
            })
        );
    }

    getLanguagesById(ids: string[]): Observable<LanguageModel[]> {
        return this.getAvailableLanguagesByDataReference().pipe(
            map(languages => {
                return languages.filter(language => ids.indexOf(language.id) >= 0);
            })
        );
    }

    autocompleteLanguages(searchString: string): Observable<LanguageModel[]> {
        return this.getAvailableLanguagesByDataReference().pipe(
            map(languages => {
                return languages.filter(language => language.name.toLocaleLowerCase().indexOf(searchString.toLocaleLowerCase()) >= 0);
            })
        );
    }

    /**
     * parses localization for arguments and replaces with provided argument
     *
     * @param text localization to be parsed
     * @param args arguments to replace in localization
     *
     * @returns parsed localization
     */
    private parseArgsFromText(text: string, args: any): string {
        if (!args) {
            return text;
        }
        const keys = Object.keys(args);
        const regEx = new RegExp('\\[\\[(' + keys.join('|') + ')\\]\\]', 'gi');
        return text.replace(regEx, (argFoundInText, argName) => {
            return this.transform(args[argName]);
        });
    }
}
